import React, { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { ArrowRight, PoundSterling } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';

const Hero = () => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 }
    }
  };

  const lineVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: 0.9 }
    }
  };

  const stats = [
    { number: '500+', label: 'Clients Served' },
    { number: '13+', label: 'Years Experience' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '24/7', label: 'Support Available' }
  ];

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-900">
      {/* Background Image (brighter, more alive) */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/9300768/pexels-photo-9300768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
          }}
        />
        {/* Softer overlay + warm glow (radial-gradient is paint-once, no expensive blur filter) */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-blue-900/65 to-slate-900/85" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(600px 320px at 50% 38%, rgba(250,204,21,0.16), transparent 70%)'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="text-center space-y-7"
        >
          {/* Logo */}
          <motion.div variants={lineVariants} className="flex items-center justify-center mb-2">
            <img src="/flexdesk-logo-white.png" alt="Flexdesk UK logo" className="h-24 sm:h-28 lg:h-32 w-auto object-contain drop-shadow-2xl" />
          </motion.div>

          {/* Save highlight badge */}
          <motion.div variants={lineVariants} className="flex justify-center">
            <div
              data-testid="hero-savings-badge"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-yellow-400 text-slate-900 font-bold text-sm sm:text-base shadow-xl shadow-yellow-500/30"
            >
              <PoundSterling className="w-5 h-5" />
              Save up to £2,000 per month per employee
            </div>
          </motion.div>

          {/* Headings */}
          <div className="space-y-3">
            <motion.h1
              variants={lineVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight"
            >
              Your Trusted <span className="text-yellow-400">Administrative</span> Partner
            </motion.h1>
          </div>

          {/* Subheading */}
          <motion.p
            variants={lineVariants}
            className="text-lg sm:text-xl text-slate-200 max-w-2xl mx-auto leading-relaxed"
          >
            Empowering businesses across the UK with expert admin support — bookkeeping,
            virtual assistants, back office and more. Excellence in every detail.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={buttonVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4"
          >
            <button
              onClick={scrollToContact}
              data-testid="hero-get-started"
              className="group px-8 py-4 bg-yellow-400 hover:bg-yellow-300 text-slate-900 rounded-xl font-bold text-lg transition-all duration-300 flex items-center gap-2 shadow-2xl shadow-yellow-500/30 hover:scale-105"
            >
              Get Started
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white/15 hover:bg-white/25 text-white rounded-xl font-semibold text-lg transition-all duration-300 border border-white/25 hover:border-yellow-400/60"
            >
              Our Services
            </button>
          </motion.div>

          {/* Stats with count-up */}
          <motion.div
            variants={buttonVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-14 max-w-5xl mx-auto"
          >
            {stats.map((stat, idx) => (
              <div
                key={idx}
                data-testid={`hero-stat-${idx}`}
                className="text-center p-6 bg-slate-900/40 rounded-2xl border border-white/10 hover:border-yellow-400/40 transition-colors duration-300"
              >
                <AnimatedCounter
                  value={stat.number}
                  className="block text-4xl lg:text-5xl font-bold text-yellow-400 mb-2"
                />
                <div className="text-sm text-slate-300">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-6 h-10 border-2 border-white/30 rounded-full p-1"
        >
          <div className="w-1.5 h-3 bg-white/70 rounded-full mx-auto" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
